import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  DollarSign,
  Plus,
  Edit,
  Trash2,
  X,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import config from '../config/config';

const EmployeeManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [benefitsDeductions, setBenefitsDeductions] = useState([]);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showBenefitModal, setShowBenefitModal] = useState(false);
  const [employeeForm, setEmployeeForm] = useState({
    id: null,
    first_name: '',
    last_name: '',
    surname: '',
    email: '',
    branch: '',
    bank_transfer_charge: '',
    nationalId: '',
    contact: '',
    basic_salary: '',
    shif_number: '',
    nssf_number: '',
    bank: '',
    account_name: '',
    account_number: '',
    is_pwd: false,
  });
  const [benefitForm, setBenefitForm] = useState({
    id: null,
    name: '',
    type: 'benefit',
    amount: '',
    is_percentage: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const token = localStorage.getItem('token');

  // Fetch employees
  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.API_URL}/api/pullleavetypes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      ////console.log('Employees API response:', response.data);
      setEmployees(response.data.data || []);
    } catch (err) {
      toast.error('Failed to fetch employees');
      //console.error('Fetch employees error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch benefits/deductions
  const fetchBenefitsDeductions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.API_URL}/api/benefits-deductions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      ////console.log('Benefits/Deductions API response:', response.data);
      setBenefitsDeductions(response.data.data || []);
    } catch (err) {
      toast.error('Failed to fetch benefits/deductions');
      //console.error('Fetch benefits/deductions error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Save employee
  const saveEmployee = async () => {
    setErrors({});
    setLoading(true);
    try {
      const payload = {
        first_name: employeeForm.first_name,
        last_name: employeeForm.last_name,
        surname: employeeForm.surname,
        branch: employeeForm.branch,
        bank_transfer_charge:employeeForm.bank_transfer_charge,
        nationalId: employeeForm.nationalId,
        email: employeeForm.email,
        contact: employeeForm.contact,
        basic_salary: Number(employeeForm.basic_salary),
        shif_number: employeeForm.shif_number,
        nssf_number: employeeForm.nssf_number,
        bank: employeeForm.bank,
        account_name: employeeForm.account_name,
        account_number: employeeForm.account_number,
        is_pwd: employeeForm.is_pwd ? 1 : 0,
      };
      
       await axios.get(config.API_URL + "/sanctum/csrf-cookie", {
        withCredentials: true,
      });

      // 2. Get CSRF token
      const getCookie = (name) => {
        const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
        if (match) return decodeURIComponent(match[2]);
        return null;
      };
      const xsrfToken = getCookie("XSRF-TOKEN");

      const response = employeeForm.id
        ? await axios.put(`${config.API_URL}/api/updateemployees/${employeeForm.id}`, payload, {
            headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-XSRF-TOKEN": xsrfToken,
          },
          withCredentials: true,
          })
        : await axios.post(`${config.API_URL}/api/saveemployees`, payload, {
           headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-XSRF-TOKEN": xsrfToken,
          },
          withCredentials: true,
          });

      ////console.log('Save employee response:', response.data);
      toast.success(employeeForm.id ? 'Employee updated' : 'Employee added');
      fetchEmployees();
      resetEmployeeForm();
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        toast.error('Failed to save employee');
      }
      //console.error('Save employee error:', err);
    } finally {
      setLoading(false);
    }
  };
 const checkTab = async (tab) => {
      if(tab==='payroll'){
        navigate('/payroll');
      }
  }
  // Save benefit/deduction
  const saveBenefitDeduction = async () => {
    setErrors({});
    setLoading(true);

    try {
      const payload = {
        name: benefitForm.name,
        type: benefitForm.type,
        amount: Number(benefitForm.amount),
        is_percentage: benefitForm.is_percentage ? 1 : 0,
        company_id: 1, // Default company ID
      };
      
      ////console.log('Benefit/Deduction payload:', payload);

        await axios.get(config.API_URL + "/sanctum/csrf-cookie", {
        withCredentials: true,
      });

      // 2. Get CSRF token
      const getCookie = (name) => {
        const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
        if (match) return decodeURIComponent(match[2]);
        return null;
      };
      const xsrfToken = getCookie("XSRF-TOKEN");

      const response = benefitForm.id
        ? await axios.put(`${config.API_URL}/api/updatebenefit/${benefitForm.id}`, payload, {
            headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-XSRF-TOKEN": xsrfToken,
          },
          withCredentials: true,
          })
        : await axios.post(`${config.API_URL}/api/storebenefit`, payload, {
            headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-XSRF-TOKEN": xsrfToken,
          },
          withCredentials: true,
          });

      ////console.log('Save benefit/deduction response:', response.data);
      toast.success(benefitForm.id ? 'Benefit/Deduction updated' : 'Benefit/Deduction added');
      fetchBenefitsDeductions();
      resetBenefitForm();
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        toast.error('Failed to save benefit/deduction');
      }
      //console.error('Save benefit/deduction error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete employee
  const deleteEmployee = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    setLoading(true);
    try {

      await axios.get(`${config.API_URL}/api/deleteemployees/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Employee deleted');
      fetchEmployees();
    } catch (err) {
      toast.error('Failed to delete employee');
      //console.error('Delete employee error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete benefit/deduction
  const deleteBenefitDeduction = async (id) => {
    if (!window.confirm('Are you sure you want to delete this benefit/deduction?')) return;
    setLoading(true);
    try {
       await axios.get(config.API_URL + "/sanctum/csrf-cookie", {
        withCredentials: true,
      });

      // 2. Get CSRF token
      const getCookie = (name) => {
        const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
        if (match) return decodeURIComponent(match[2]);
        return null;
      };
      const xsrfToken = getCookie("XSRF-TOKEN");
      await axios.delete(`${config.API_URL}/api/delete-benefit/${id}`, {
         headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-XSRF-TOKEN": xsrfToken,
          },
          withCredentials: true,
      });
      toast.success('Benefit/Deduction deleted');
      fetchBenefitsDeductions();
    } catch (err) {
      toast.error('Failed to delete benefit/deduction');
      //console.error('Delete benefit/deduction error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Edit employee
  const editEmployee = (employee) => {
    setEmployeeForm({
      id: employee.id,
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      surname: employee.surname || '',
      email: employee.email || '',
      branch: employee.branch || '',
      nationalId: employee.nationalId || '',
      contact: employee.contact || '',
      basic_salary: employee.basic_salary || '',
      shif_number: employee.shif_number || '',
      nssf_number: employee.nssf_number || '',
      bank: employee.bank || '',
      bank_transfer_charge: employee.bank_transfer_charge || '',
      account_name: employee.account_name || '',
      account_number: employee.account_number || '',
      is_pwd: !!employee.is_pwd,
    });
    setShowEmployeeModal(true);
  };

  // Edit benefit/deduction
  const editBenefitDeduction = (item) => {
    setBenefitForm({
      id: item.id,
      days:item.days,
      name: item.name || '',
      type: item.type || 'benefit',
      amount: item.amount || '',
      is_percentage: !!item.is_percentage,
    });
    setShowBenefitModal(true);
  };

  // Reset forms
  const resetEmployeeForm = () => {
    setEmployeeForm({
      id: null,
      first_name: '',
      last_name: '',
      surname: '',
      email: '',
      branch: '',
      nationalId: '',
      bank_transfer_charge: '',
      contact: '',
      basic_salary: '',
      shif_number: '',
      nssf_number: '',
      bank: '',
      account_name: '',
      account_number: '',
      is_pwd: false,
    });
    setShowEmployeeModal(false);
    setErrors({});
  };

  const resetBenefitForm = () => {
    setBenefitForm({
      id: null,
      name: '',
      type: 'benefit',
      days:0,
      amount: '',
      is_percentage: false,
    });
    setShowBenefitModal(false);
    setErrors({});
  };

  useEffect(() => {
    if (token) {
      fetchEmployees();
      fetchBenefitsDeductions();
    } else {
      toast.error('Please log in to access this page');
    }
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Users className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Leave Type Management</h1>
                <p className="text-sm text-gray-600">Manage leave types</p>
              </div>
            </div>
            <div className="flex space-x-4">
              
              <button
                onClick={() => setShowBenefitModal(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add Leave Type</span>
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-8 border-b border-gray-200 mb-6">
          {['employees'].map(tab => (
            <button
              key={tab}
              onClick={() =>{checkTab(tab); setActiveTab(tab);}}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Leave Types
            </button>
          ))}
        </div>

        {/* Employees Table */}
        {activeTab === 'employees' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="p-6">
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : employees.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Name</th>
                         <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">No. of Days</th>
                           <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Applicable To (Gender)</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Created At</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {employees.map(employee => (
                        <tr key={employee.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {employee.name || 'N/A'}
                          </td>
                           <td className="px-6 py-4 text-sm text-gray-900">{employee.days}</td>
                             <td className="px-6 py-4 text-sm text-gray-900">{employee.gender}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{employee.created_at}</td>
                          
                          <td className="px-6 py-4 text-sm">
                            <button

                              onClick={() => editBenefitDeduction(employee)}
                              className="text-blue-600 hover:text-blue-800 mr-4"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteEmployee(employee.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-600">No employees found</div>
              )}
            </div>
          </div>
        )}

        {/* Benefits/Deductions Table */}
        {activeTab === 'benefits_deductions' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="p-6">
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : benefitsDeductions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Percentage</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {benefitsDeductions.map(item => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-900">{item.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 capitalize">{item.type}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {item.is_percentage
                              ? `${item.amount}%`
                              : `KES ${Number(item.amount).toLocaleString()}`}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {item.is_percentage ? 'Yes' : 'No'}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <button
                              onClick={() => editBenefitDeduction(item)}
                              className="text-blue-600 hover:text-blue-800 mr-4"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteBenefitDeduction(item.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-600">No benefits/deductions found</div>
              )}
            </div>
          </div>
        )}

        {/* Employee Modal */}
        {showEmployeeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {employeeForm.id ? 'Edit Employee' : 'Add Employee'}
                </h3>
                <button onClick={resetEmployeeForm} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { label: 'First Name', key: 'first_name', type: 'text' },
                  { label: 'Last Name', key: 'last_name', type: 'text' },
                  { label: 'Surname', key: 'surname', type: 'text' },
                  { label: 'Email', key: 'email', type: 'email' },
                  { label: 'Contact', key: 'contact', type: 'tel' },
                   { label: 'National Id', key: 'nationalId', type: 'tel' },
                  { label: 'Salary (KES)', key: 'basic_salary', type: 'number' },
                  { label: 'SHIF Number', key: 'shif_number', type: 'text' },
                  { label: 'NSSF Number', key: 'nssf_number', type: 'text' },
                  { label: 'Bank', key: 'bank', type: 'text' },
                  { label: 'Branch', key: 'branch', type: 'text' },
                  { label: 'Account Name', key: 'account_name', type: 'text' },
                  { label: 'Account Number', key: 'account_number', type: 'text' },
                  { label: 'Bank Transfer Charges', key: 'bank_transfer_charge', type: 'number' },
                ].map(field => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                    <input
                      type={field.type}
                      value={employeeForm[field.key]}
                      onChange={e => setEmployeeForm({ ...employeeForm, [field.key]: e.target.value })}
                      className={`w-full px-3 py-2 border ${
                        errors[field.key] ? 'border-red-300' : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    />
                    {errors[field.key] && (
                      <p className="text-xs text-red-600 mt-1">{errors[field.key][0]}</p>
                    )}
                  </div>
                ))}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={employeeForm.is_pwd}
                    onChange={e => setEmployeeForm({ ...employeeForm, is_pwd: e.target.checked })}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label className="ml-2 text-sm font-medium text-gray-700">Person with Disability</label>
                </div>
              </div>
              <div className="flex space-x-4 mt-6">
                <button
                  onClick={resetEmployeeForm}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEmployee}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Benefit/Deduction Modal */}
        {showBenefitModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {benefitForm.id ? 'Edit Leave Type' : 'Add Leave Type'}
                </h3>
                <button onClick={resetBenefitForm} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={benefitForm.name}
                    onChange={e => setBenefitForm({ ...benefitForm, name: e.target.value })}
                    className={`w-full px-3 py-2 border ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  />
                  {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name[0]}</p>}
                </div>

                 <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Days</label>
                  <input
                    type="tel"
                    value={benefitForm.days}
                    onChange={e => setBenefitForm({ ...benefitForm, name: e.target.value })}
                    className={`w-full px-3 py-2 border ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  />
                  {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name[0]}</p>}
                </div>
               
              </div>
              <div className="flex space-x-4 mt-6">
                <button
                disabled="true"
                  onClick={resetBenefitForm}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={saveBenefitDeduction}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeManagement;